import { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { getAnalyticsConsent, setAnalyticsConsent } from '../utils/analyticsConsent';
import { Link } from 'react-router-dom';
import './AnalyticsConsentBanner.css';

/**
 * アクセス解析の同意バナー
 * 同意未選択時のみ表示。同意する/拒否するで localStorage に保存し、全利用者に表示する。
 */
const AnalyticsConsentBanner: React.FC = () => {
  const lang = useLanguage();
  const [consent, setConsentState] = useState<boolean | null>(() => getAnalyticsConsent());
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setConsentState(getAnalyticsConsent());
  }, []);

  useEffect(() => {
    setIsVisible(consent === null);
  }, [consent]);

  const handleAccept = () => {
    setAnalyticsConsent(true);
    setConsentState(true);
    setIsVisible(false);
  };

  const handleReject = () => {
    setAnalyticsConsent(false);
    setConsentState(false);
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  const privacyUrl = `/${lang}/privacy-policy`;
  const message =
    lang === 'ja'
      ? '当サイトでは、サービス向上のためアクセス解析にご協力をお願いしています。端末情報（スマートフォン／タブレット等の種類）の取得について、同意をお願いします。'
      : 'This site asks for your cooperation with access analytics to improve our service. Please consent to collecting device information (e.g. smartphone/tablet type).';
  const privacyLinkText = lang === 'ja' ? 'プライバシーポリシー' : 'Privacy Policy';
  const settingsHint =
    lang === 'ja' ? '設定でいつでも変更できます。' : 'You can change this in Settings at any time.';
  const acceptLabel = lang === 'ja' ? '同意する' : 'Accept';
  const rejectLabel = lang === 'ja' ? '拒否する' : 'Reject';

  return (
    <div
      className="analytics-consent-banner"
      role="dialog"
      aria-label={lang === 'ja' ? 'アクセス解析の同意' : 'Analytics consent'}
      aria-modal="false"
    >
      <div className="analytics-consent-banner__content">
        <p className="analytics-consent-banner__message">
          {message}{' '}
          <Link to={privacyUrl} className="analytics-consent-banner__link">
            {privacyLinkText}
          </Link>
          {' · '}
          <span className="analytics-consent-banner__hint">{settingsHint}</span>
        </p>
        <div className="analytics-consent-banner__actions">
          <button
            type="button"
            className="analytics-consent-banner__button analytics-consent-banner__button--reject"
            onClick={handleReject}
            aria-label={rejectLabel}
          >
            {rejectLabel}
          </button>
          <button
            type="button"
            className="analytics-consent-banner__button analytics-consent-banner__button--accept"
            onClick={handleAccept}
            aria-label={acceptLabel}
          >
            {acceptLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsConsentBanner;
